import { isNative, isOverseas, isProd, isWeb } from '@whelk/const/src/env';
import { MAIN_ROBOT_INFO } from '@whelk/const/src/robot';

import { nativeBridge } from './bridge/callNativeBridge';
import { browserName, osName } from './checkUA';

export const trackOrigin = process?.env?.NEXT_PUBLIC_ENV ?? '';

// 定义和埋点保持一致
export const CHAT_SEND_TYPE = {
  TEXT: 'text',
  PIC: 'pic',
  FILE: 'file',
  VOICE: 'voice',
  TEL: 'oncall',
  MULTI: 'multimodal',
  GOODS: 'goods',
  CODE: 'cicode',
} as const;

export const RECOMMEND_TYPE = {
  NORMAL: 'normal',
  STARTCHATDEMO: 'start_chat_demo',
  CONTINUECHATDEMO: 'continue_chat_demo',
} as const;

export type chatSendTypeEnum = (typeof CHAT_SEND_TYPE)[keyof typeof CHAT_SEND_TYPE];

export type recommendTypeEnum = (typeof RECOMMEND_TYPE)[keyof typeof RECOMMEND_TYPE];

// type PageName = 'home_page' | 'chat_page' | 'library_page' | 'chat_history_page';

type CustomEvent = any;

export type TrackParams = Record<string, string | number | boolean | null>;

const OVERSEAS_DOMAIN = 'https://data.hailuo.ai';
const INLAND_DOMAIN = 'https://data.hailuoai.com';

const MMX_SERVER_URL_MAP = {
  overseas: {
    prod: `${OVERSEAS_DOMAIN}/meerkat-reporter/api/report?project=Inspo`,
    test: 'https://bigdata-test.talkie-ai.com/meerkat-reporter/api/report?project=Inspo',
  },
  inland: {
    prod: `${INLAND_DOMAIN}/meerkat-reporter/api/report?project=Inspo`,
    test: 'https://bigdata-test.xingyeai.com/meerkat-reporter/api/report?project=Inspo',
  },
};

const MMX_SERVER_URL =
  MMX_SERVER_URL_MAP[isOverseas ? 'overseas' : 'inland'][isProd ? 'prod' : 'test'];

const NEED_SEND_TO_SENSORS_EVENTS = ['chat_send_success', 'login_result', 'call_end_status'];

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
let sensors: typeof import('sa-sdk-javascript').default | undefined;

export async function trackCustomEvent(event: CustomEvent) {
  let data = { bot_id: MAIN_ROBOT_INFO.robotIDStr, ...event };
  const eventID = data.id;
  if (!NEED_SEND_TO_SENSORS_EVENTS.includes(eventID)) {
    data = {
      ...data,
      $option: { server_url: MMX_SERVER_URL },
    };
  }
  const eventData = data as TrackParams;
  const params = {
    events: [
      {
        id: eventID,
        params: JSON.stringify(eventData),
      },
    ],
  };
  delete eventData.id;
  if (isNative) {
    nativeBridge.callNative({
      methodName: 'trackEvents',
      params,
    });
  } else {
    if (!sensors) await importSensorsSDK();
    sensors?.track(eventID, eventData);
  }
}

// 神策SDK包在module-level就使用了浏览器API, 不能在server端导入, 需要动态import
export async function importSensorsSDK() {
  sensors = (await import('sa-sdk-javascript')).default;
}

export async function trackSCGlobalProperty(property: TrackParams) {
  if (!sensors) await importSensorsSDK();
  sensors?.registerPage(property);
  sensors?.quick('autoTrack', property);
}

export async function trackLogin(id: string) {
  if (!sensors) await importSensorsSDK();
  sensors?.login(id);
}

export const initSensors = async () => {
  await importSensorsSDK();
  // 使用 use 初始化渠道插件
  sensors?.use('SensorsChannel', {});

  sensors?.init({
    // 默认双发
    server_url: [MMX_SERVER_URL],
    is_track_single_page: true, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
    use_client_time: true,
    show_log: process.env.NEXT_PUBLIC_BUILD_ENV !== 'prod' ? true : false, // 非线上环境都打开
    send_type: 'ajax',
    heatmap: {
      //是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
      clickmap: 'default',
      //是否开启触达图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
      scroll_notice_map: 'not_collect',
    },
  });

  sensors?.registerPage({
    current_url: location.href,
    referrer: document.referrer,
    project_name: 'chat',
    client: isWeb ? 'web' : 'applets',
    web_device: process.env.NEXT_PUBLIC_ENV,
    os: osName,
    browser: browserName,
    env: process.env.NEXT_PUBLIC_BUILD_ENV,
  });
};
